import React, { useEffect, useState } from 'react';
import api from "../../utils/axiosInstance";
import "@fontsource/oswald";
import Select from 'react-select';
import HeaderBlue from '../../components/HeaderBlue';
import FooterBlue from '../../components/FooterBlue';

const CarrierRegistration = () => {
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [formData, setFormData] = useState({
        companyName: '',
        trailer: '',
        contactName: '',
        jobTitle: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        website: '',
        companyDesc: '',
        companyLogo: null,
        password: '',
        confirmPassword: '',
        terms: false
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, files, checked } = e.target;

        setFormData({
            ...formData,
            [name]: type === 'file' ? files[0] :
                type === 'checkbox' ? checked : value
        });
    };

    const trailerOptions = [
        { value: 'Van', label: 'Van' },
        { value: 'Flatbed', label: 'Flatbed' },
        { value: 'Reefer', label: 'Reefer' },
        { value: 'Tanker', label: 'Tanker' },
        { value: 'Other', label: 'Other' },
    ];

    const validateForm = () => {
        const newErrors = {};
        // Required fields validation
        if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
        if (!formData.trailer || formData.trailer.length === 0) newErrors.trailer = 'At least one Equipment Hauled type is required';
        if (!formData.contactName.trim()) newErrors.contactName = 'Contact person is required';
        if (!formData.email.trim()) newErrors.email = 'Email is required';
        if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
        if (!formData.address.trim()) newErrors.address = 'Address is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state) newErrors.state = 'State is required';
        if (!formData.zip.trim()) newErrors.zip = 'ZIP code is required';
        if (!formData.password) newErrors.password = 'Password is required';
        if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirm password is required';
        if (!formData.terms) newErrors.terms = 'You must agree to the terms';

        // Email validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Phone validation
        if (formData.phone && !/^(\(\d{3}\)\s?\d{3}-\d{4})$/.test(formData.phone)) {
            newErrors.phone = 'Invalid phone number';
        }

        // Password validation
        if (formData.password && formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (formData.password && formData.confirmPassword &&
            formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => {
                if (key === 'companyLogo' && value) {
                    data.append('companyLogo', value);
                } else if (value !== undefined && value !== null) {
                    data.append(key, value);
                }
            });

            await api.post('/pendingcarriers', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setShowSuccessModal(true);
        } catch (err) {
            console.error('Registration failed:', err.response?.data || err.message);
            alert('Registration failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, '').slice(0, 10);
        const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (match) {
            const [, area, central, line] = match;
            if (central) {
                return `(${area}) ${central}${line ? `-${line}` : ''}`;
            }
            return area;
        }
        return value;
    };

    const redirectToHome = () => {
        window.location.href = '/';
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#ffffff',
            borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
            paddingLeft: '2rem',
            minHeight: '3rem', // py-4 equivalent
            boxShadow: state.isFocused ? '0 0 0 2px rgba(251, 191, 36, 0.5)' : 'none', // yellow ring focus
            borderRadius: '0.375rem', // rounded-md
            color: '#000000',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e5e7eb', // Tailwind: bg-gray-200
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#000000', // black text inside selected tags
        }),
        input: (provided) => ({
            ...provided,
            color: '#000000', // input text color
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6b7280', // Tailwind: text-gray-500
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#e5e7eb' : '#ffffff', // gray on hover, white otherwise
            color: '#000000',
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#ffffff', // white dropdown
            zIndex: 50,
        }),
    };

    return (
        <div className="bg-gray-100 min-h-screen flex flex-col">
            <HeaderBlue />

            {/* Hero Section */}
            <section
                className="w-full md:py-40 xl:py-60 relative bg-cover bg-top"
                style={{
                    backgroundImage: "url('./assets/employerregister1.webp')"
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-white/10 z-0"></div>
                <div className="md:px-40 relative z-10">
                    <h2 className="text-white text-6xl font-oswald drop-shadow-lg mb-5">CARRIER REGISTRATION</h2>
                    <p className="text-white text-xl interfont font-oswald">
                        Create a Carrier account to efficiently connect with, manage, and <br />
                        hire qualified drivers, boosting operational success.
                    </p>
                </div>
            </section>

            {/* Main Content */}
            <div className="container max-w-7xl mx-auto px-5 py-10">
                <div className="registration-container flex flex-col lg:flex-row gap-10 bg-white rounded-lg overflow-hidden shadow-xl">
                    <div className="form-side flex-1 p-10">
                        <form id="employerRegistrationForm" className="space-y-6" onSubmit={handleSubmit}>
                            <div className="form-row flex flex-col md:flex-row gap-5">
                                <div className="form-group flex-1">
                                    <label htmlFor="companyName" className="block font-medium text-dark mb-2">Company Name *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-building absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="text"
                                            id="companyName"
                                            name="companyName"
                                            placeholder="Enter company name"
                                            value={formData.companyName}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.companyName ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.companyName && <p className="text-blue-500 text-sm mt-1">{errors.companyName}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="trailer" className="block font-medium text-dark mb-2">
                                        Equipment Hauled *
                                    </label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-truck absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"></i>
                                        <div className="w-full">
                                            <Select
                                                id="trailer"
                                                isMulti
                                                placeholder="Select multiple if required"
                                                name="trailer"
                                                options={trailerOptions}
                                                value={trailerOptions.filter(option => formData.trailer.includes(option.value))}
                                                onChange={(selectedOptions) => {
                                                    const values = selectedOptions.map(option => option.value);
                                                    setFormData({ ...formData, trailer: values });
                                                }}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                styles={customSelectStyles}
                                            />
                                            {errors.trailer && (
                                                <p className="text-blue-500 text-sm mt-1">{errors.trailer}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row flex flex-col md:flex-row gap-5">
                                <div className="form-group flex-1">
                                    <label htmlFor="contactName" className="block font-medium text-dark mb-2">Contact Person *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-user absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="text"
                                            id="contactName"
                                            name="contactName"
                                            placeholder="Full name"
                                            value={formData.contactName}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.contactName ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.contactName && <p className="text-blue-500 text-sm mt-1">{errors.contactName}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="jobTitle" className="block font-medium text-dark mb-2">Job Title</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-briefcase absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="text"
                                            id="jobTitle"
                                            name="jobTitle"
                                            placeholder="Your position"
                                            value={formData.jobTitle}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.jobTitle ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.jobTitle && <p className="text-blue-500 text-sm mt-1">{errors.jobTitle}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="form-row flex flex-col md:flex-row gap-5">
                                <div className="form-group flex-1">
                                    <label htmlFor="email" className="block font-medium text-dark mb-2">Email Address *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="company@email.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.email ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.email && <p className="text-blue-500 text-sm mt-1">{errors.email}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="phone" className="block font-medium text-dark mb-2">Phone Number *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-phone absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            placeholder="(123) 456-7890"
                                            value={formData.phone}
                                            onChange={(e) => {
                                                const formatted = formatPhoneNumber(e.target.value);
                                                handleChange({ target: { name: 'phone', value: formatted } });
                                            }}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.phone ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.phone && <p className="text-blue-500 text-sm mt-1">{errors.phone}</p>}
                                    </div>
                                </div>

                            </div>

                            <div className="form-group">
                                <label htmlFor="address" className="block font-medium text-dark mb-2">Company Address *</label>
                                <div className="input-with-icon relative">
                                    <i className="fas fa-map-marker-alt absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        placeholder="Street address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className={`w-full pl-12 pr-4 py-4 border ${errors.address ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                    />
                                    {errors.address && <p className="text-blue-500 text-sm mt-1">{errors.address}</p>}
                                </div>
                            </div>

                            <div className="form-row flex flex-col md:flex-row gap-5">
                                <div className="form-group flex-1">
                                    <label htmlFor="city" className="block font-medium text-dark mb-2">City *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-city absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            placeholder="City"
                                            value={formData.city}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.city ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.city && <p className="text-blue-500 text-sm mt-1">{errors.city}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="state" className="block font-medium text-dark mb-2">State *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-flag-usa absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <select
                                            id="state"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.state ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        >
                                            <option value="">Select state</option>
                                            <option value="Alabama">Alabama</option>
                                            <option value="Alaska">Alaska</option>
                                            <option value="Arizona">Arizona</option>
                                            <option value="Arkansas">Arkansas</option>
                                            <option value="California">California</option>
                                            <option value="Colorado">Colorado</option>
                                            <option value="Connecticut">Connecticut</option>
                                            <option value="Delaware">Delaware</option>
                                            <option value="Florida">Florida</option>
                                            <option value="Georgia">Georgia</option>
                                            <option value="Hawaii">Hawaii</option>
                                            <option value="Idaho">Idaho</option>
                                            <option value="Illinois">Illinois</option>
                                            <option value="Indiana">Indiana</option>
                                            <option value="Iowa">Iowa</option>
                                            <option value="Kansas">Kansas</option>
                                            <option value="Kektucky">Kentucky</option>
                                            <option value="Louisiana">Louisiana</option>
                                            <option value="Maine">Maine</option>
                                            <option value="Maryland">Maryland</option>
                                            <option value="Massachusetts">Massachusetts</option>
                                            <option value="Michigan">Michigan</option>
                                            <option value="Minnesota">Minnesota</option>
                                            <option value="Mississippi">Mississippi</option>
                                            <option value="Missouri">Missouri</option>
                                            <option value="Montana">Montana</option>
                                            <option value="Nebraska">Nebraska</option>
                                            <option value="Nevada">Nevada</option>
                                            <option value="New Hampshire">New Hampshire</option>
                                            <option value="New Jersey">New Jersey</option>
                                            <option value="New Mexico">New Mexico</option>
                                            <option value="New York">New York</option>
                                            <option value="North Carolina">North Carolina</option>
                                            <option value="North Dakota">North Dakota</option>
                                            <option value="Ohio">Ohio</option>
                                            <option value="Oklahoma">Oklahoma</option>
                                            <option value="Oregon">Oregon</option>
                                            <option value="Pennsylvania">Pennsylvania</option>
                                            <option value="Rhode Island">Rhode Island</option>
                                            <option value="South Carolina">South Carolina</option>
                                            <option value="South Dakota">South Dakota</option>
                                            <option value="Tennessee">Tennessee</option>
                                            <option value="Texas">Texas</option>
                                            <option value="Utah">Utah</option>
                                            <option value="Vermont">Vermont</option>
                                            <option value="Viginia">Virginia</option>
                                            <option value="Washington">Washington</option>
                                            <option value="West Virginia">West Virginia</option>
                                            <option value="Wisconsin">Wisconsin</option>
                                            <option value="Wyoming">Wyoming</option>
                                        </select>
                                        {errors.state && <p className="text-blue-500 text-sm mt-1">{errors.state}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="zip" className="block font-medium text-dark mb-2">ZIP Code *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-mail-bulk absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="text"
                                            id="zip"
                                            name="zip"
                                            placeholder="ZIP code"
                                            value={formData.zip}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.zip ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.zip && <p className="text-blue-500 text-sm mt-1">{errors.zip}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="website" className="block font-medium text-dark mb-2">Company Website</label>
                                <div className="input-with-icon relative">
                                    <i className="fas fa-globe absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                    <input
                                        type="text"
                                        id="website"
                                        name="website"
                                        placeholder="https://yourcompany.com"
                                        value={formData.website}
                                        onChange={handleChange}
                                        className="w-full pl-12 pr-4 py-4 border border-border rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200"
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="companyDesc" className="block font-medium text-dark mb-2">Company Description</label>
                                <div className="input-with-icon relative">
                                    <i className="fas fa-file-alt absolute left-4 top-5 text-gray"></i>
                                    <textarea
                                        id="companyDesc"
                                        name="companyDesc"
                                        placeholder="Brief description of your company"
                                        rows="3"
                                        value={formData.companyDesc}
                                        onChange={handleChange}
                                        className={`w-full pl-12 pr-4 py-4 border ${errors.companyDesc ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                    ></textarea>
                                    {errors.companyDesc && <p className="text-blue-500 text-sm mt-1">{errors.companyDesc}</p>}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="companyLogo" className="block font-medium text-dark mb-2">Company Logo</label>
                                <div className="input-with-icon relative">
                                    <i className="fas fa-image absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                    <input
                                        type="file"
                                        id="companyLogo"
                                        name="companyLogo"
                                        accept="image/*"
                                        onChange={handleChange}
                                        className="w-full pl-12 pr-4 py-4 border border-border rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200"
                                    />
                                </div>
                            </div>

                            <div className="form-row flex flex-col md:flex-row gap-5">
                                <div className="form-group flex-1">
                                    <label htmlFor="password" className="block font-medium text-dark mb-2">Password *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            placeholder="Create password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.password ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.password && <p className="text-blue-500 text-sm mt-1">{errors.password}</p>}
                                    </div>
                                </div>
                                <div className="form-group flex-1">
                                    <label htmlFor="confirmPassword" className="block font-medium text-dark mb-2">Confirm Password *</label>
                                    <div className="input-with-icon relative">
                                        <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray"></i>
                                        <input
                                            type="password"
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            placeholder="Confirm password"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            className={`w-full pl-12 pr-4 py-4 border ${errors.confirmPassword ? 'border-blue-500' : 'border-border'} rounded-md text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-yellow-200`}
                                        />
                                        {errors.confirmPassword && <p className="text-blue-500 text-sm mt-1">{errors.confirmPassword}</p>}
                                    </div>
                                </div>
                            </div>

                            <div className="terms flex items-center gap-3 mb-6">
                                <input
                                    type="checkbox"
                                    id="terms"
                                    name="terms"
                                    checked={formData.terms}
                                    onChange={handleChange}
                                    className={`h-5 w-5 text-primary focus:ring-primary border-border rounded ${errors.terms ? 'border-blue-500' : ''}`}
                                />
                                <label htmlFor="terms" className="text-dark">
                                    I agree to the <a href="/terms-of-use" className="text-primary hover:underline ">Terms of Service</a> and
                                    <a href="/privacy-policy" className="text-primary hover:underline"> Privacy Policy</a>
                                </label>
                                {errors.terms && <p className="text-blue-500 text-sm ml-2">{errors.terms}</p>}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`btn btn-primary bg-primary text-white w-full py-4 px-8 rounded-md font-semibold flex items-center justify-center gap-3 hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin"></i> Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-user-plus"></i> Create Carrier Account
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Info side remains unchanged */}
                    <div
                        className="info-side relative flex flex-col justify-start p-10 bg-cover bg-center min-h-[600px] text-white"
                        style={{
                            backgroundImage: "url('./assets/employerreg.webp')"
                        }}
                    >
                        <div className="absolute inset-0 bg-black/27 z-0"></div>
                        <div className="relative z-10">
                            <h3 className="text-3xl font-bold mb-5">Why Register as a Carrier?</h3>
                            <p className="mb-8">Join thousands of companies finding qualified drivers through our platform.</p>
                            <ul className="space-y-5">
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <i className="fas fa-truck-loading text-xl text-white"></i>
                                    </div>
                                    <div>
                                        <strong className="font-semibold">Find Qualified Drivers</strong>
                                        <p>Access our extensive database of professional drivers</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <i className="fas fa-bullhorn text-xl text-white"></i>
                                    </div>
                                    <div>
                                        <strong className="font-semibold">Post Jobs Easily</strong>
                                        <p>Create and manage job postings in minutes</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <i className="fas fa-filter text-xl text-white"></i>
                                    </div>
                                    <div>
                                        <strong className="font-semibold">Advanced Filtering</strong>
                                        <p>Filter candidates by experience, license type, and location</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                        <i className="fas fa-shield-alt text-xl text-white"></i>
                                    </div>
                                    <div>
                                        <strong className="font-semibold">Verified Profiles</strong>
                                        <p>All drivers undergo background and license verification</p>
                                    </div>
                                </li>
                            </ul>
                            <div className="highlight bg-black/10 p-5 rounded-lg mt-8 font-medium">
                                <i className="fas fa-lightbulb mr-2"></i>
                                Pro Tip: Complete your company profile to attract more <br />
                                qualified drivers and receive up to 5x more applications.
                            </div>
                        </div>

                    </div>
                </div>
            </div>
            <FooterBlue />

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                    <div className="bg-white max-w-2xl mx-auto rounded-xl shadow-xl p-10 relative border-t-8 border-danger text-center">
                        <div className="text-success text-6xl mb-6">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-black mb-4">
                            Welcome to <span className="text-blue-700">Fleety</span> Portal!
                        </h2>
                        <p className="text-gray-700 mb-6">
                            "Carrier registered successfully! Please check your email for confirmation."
                        </p>
                        <button
                            onClick={redirectToHome}
                            className="mt-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow font-semibold text-lg transition duration-300"
                        >
                            Go to Home
                        </button>

                    </div>
                </div>
            )}
        </div>
    );
};

export default CarrierRegistration;